import type { ProjectSchemaJSON } from '@takeshape/schema';
import { getShapeById, workflowsEnabled } from '@takeshape/schema';
import { assert } from '@takeshape/util';
import getSingletonContentId from 'app/common/utils/get-singleton-content-id';
import {
  getFormConfigByLocale as baseGetFormConfigByLocale,
  getApiVersion,
  getInputSchema,
  getLocale,
  getProjectId,
  getShapeIdByName,
  type SchemaSlice
} from 'app/schema';
import { fromJS } from 'immutable';
import omit from 'lodash/omit';
import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';
import { getUserId } from '../auth/selectors.js';
import { getWorkflowByShapeId } from '../workflows/selectors.js';
import { NAME } from './constants.js';
import { defaultContentState, defaultSession } from './reducer.js';
import type {
  AllContentState,
  Content,
  ContentId,
  ContentSlice,
  ContentState,
  EditingSessionInfo,
  LocaleState
} from './types';
import { addDiscriminatorValues } from './utils/add-discriminator-values.js';

export const getState = (state: ContentSlice): AllContentState => state[NAME];

const getSource = (_: unknown, props: { source: string }): string => props.source;

const getShapeId = (state: ContentSlice & SchemaSlice, props: { shapeId?: string; shapeName?: string }): string => {
  const shapeId = props.shapeId
    ? props.shapeId
    : props.shapeName && getShapeIdByName(state, { shapeName: props.shapeName });
  if (!shapeId) {
    throw new Error(`Unable to find shape ${props.shapeName ?? props.shapeId}`);
  }

  return shapeId;
};

export type GetContentIdProps = {
  contentId?: string;
  shapeName?: string;
  shapeId?: string;
};

export const getContentId = (state: ContentSlice & SchemaSlice, props: GetContentIdProps): string | undefined =>
  props.contentId === 'edit' ? getSingletonContentId(getProjectId(state), getShapeId(state, props)) : props.contentId;

export const getContentStateCacheKey = (state: ContentSlice & SchemaSlice, props: GetContentIdProps): string => {
  const contentId = props.contentId && getContentId(state, props);
  return contentId ?? 'new content';
};

export const getEditingSession = createSelector(
  [getState, getUserId, getContentId],
  (state, userId, contentId): EditingSessionInfo => {
    const session = contentId ? state.editingSessions[contentId] : undefined;
    if (session) {
      return {
        ...session,
        hasLock: session.lock?.user.id === userId
      };
    }

    return { ...defaultSession, hasLock: false };
  }
);

export const getIsLocked = createSelector(getEditingSession, (editingSession) => editingSession.hasLock);

export const getInactiveLocks = createSelector([getState, getUserId], (state, userId): ContentId[] => {
  return Object.values(state.editingSessions)
    .filter((session) => !session.active && session.lock?.user.id === userId)
    .map((session) => {
      assert(session.contentId, 'Editing session missing contentId');
      assert(session.shapeId, 'Editing session missing shapeId');
      return {
        contentId: session.contentId,
        shapeId: session.shapeId
      };
    });
});

const notFound = defaultContentState;

const userFields = ['_createdBy', '_updatedBy'];
function normalizeDataForEditing(
  projectSchema: ProjectSchemaJSON,
  shapeId: string,
  contentState: ContentState
): ContentState {
  const shape = getShapeById(projectSchema, shapeId);

  assert(shape, `Unable to find shape for id ${shapeId}`);

  return {
    ...contentState,
    data: contentState.data && addDiscriminatorValues(projectSchema, shape.schema, omit(contentState.data, userFields))
  };
}

export const getSingletonContentState = createCachedSelector(
  [getInputSchema, getState, getShapeId, getLocale, getContentId],
  (projectSchema, state, shapeId, locale) =>
    normalizeDataForEditing(
      projectSchema,
      shapeId,
      (state.contentByContentType[shapeId] as LocaleState)?.[locale] || notFound
    )
)(getContentStateCacheKey);

export const getContentState = createCachedSelector(
  [getInputSchema, getState, getShapeId, getContentId, getLocale],

  (projectSchema, state, shapeId, contentId, locale) =>
    normalizeDataForEditing(
      projectSchema,
      shapeId,
      (state.contentByContentType[shapeId] as Record<string, LocaleState>)?.[contentId ?? '']?.[locale] || notFound
    )
)(getContentStateCacheKey);

export const getCreatingState = createSelector([getState, getSource], (state, source) =>
  state.creating.includes(source)
);

export const getIsSaving = createSelector(
  [getContentState, getCreatingState],
  (contentState, isCreating) => contentState.updating || isCreating
);

export const getDefaultContent = createSelector(
  getApiVersion,
  getWorkflowByShapeId,
  (apiVersion, workflow): Content =>
    workflowsEnabled(apiVersion)
      ? { _status: workflow.steps[0].name }
      : {
          _enabled: true,
          _enabledAt: new Date().toISOString()
        }
);

export const getFormConfigByLocale = createSelector(baseGetFormConfigByLocale, (config) => fromJS(config));
